import {
  FileArchiveIcon,
  FileChartColumnIcon,
  FileIcon,
  FileMusicIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileTypeCornerIcon,
  FileVideoCameraIcon,
  PresentationIcon,
} from 'lucide-react'

import {
  CSV,
  DOC,
  DOCX,
  GZIP,
  ODP,
  ODS,
  ODT,
  PDF,
  PPT,
  PPTX,
  RAR,
  TAR,
  XLS,
  XLSX,
  ZIP,
  ZIP7,
} from '@/constants'
import { NodeViewWrapper } from '@tiptap/react'

import { FileNodeAttributes } from '~shared/richtext/nodes/file.node'

import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '@/features/richtext/tiptap.helpers'

export function FileNodeViewLive({ node }: NodeViewLiveProps) {
  return (
    <NodeViewWrapper>
      <FileNodeComponent node={node} />
    </NodeViewWrapper>
  )
}

export function FileNodeViewStatic({ node }: NodeViewStaticProps) {
  return <FileNodeComponent node={node} />
}

/**
 * Custom Tiptap node that renders uploaded files.
 *
 * This component displays a file icon and information, and allows users to
 * download the file by clicking a button.
 *
 * @note Not exported, use the static or live wrappers depending on usecase.
 */
function FileNodeComponent({ node }: NodeComponentProps) {
  const attrs = node.attrs as FileNodeAttributes // TODO: validate instead of assert

  const IconComponent = getLFileTypeIcon(attrs.type)

  return (
    <a
      href={attrs.url}
      target="_blank"
      rel="noreferrer"
      className="my-2 w-full no-underline! text-white! bg-white/10 hover:bg-white/20 py-1 px-2 rounded-md shadow-sm flex items-center gap-1.5"
      title="Click to download"
      draggable={false} // Prevent native dragging (not draghandle)
      tabIndex={-1}
    >
      <IconComponent className="flex-none size-7.5 stroke-1" />

      <div className="grow text-left leading-snug overflow-hidden">
        <div className="font-semibold truncate">{attrs.filename}</div>
        <div className="text-muted-foreground text-xs truncate">
          {attrs.size}
        </div>
      </div>
    </a>
  )
}

function getLFileTypeIcon(filetype: string) {
  switch (filetype) {
    case PDF:
      return FileTextIcon
    case CSV:
      return FileSpreadsheetIcon
    case DOC || DOCX || ODT:
      return FileTypeCornerIcon
    case XLS || XLSX || ODS:
      return FileChartColumnIcon
    case PPT || PPTX || ODP:
      return PresentationIcon
    case ZIP || ZIP7 || RAR || GZIP || TAR:
      return FileArchiveIcon
  }

  if (filetype.startsWith('audio')) return FileMusicIcon
  if (filetype.startsWith('video')) return FileVideoCameraIcon

  return FileIcon
}
